/**
 * Participant view: renders the active activity and submits responses
 * through the SAME per-type endpoints + participant-token auth the web
 * join page uses (the old generic submit endpoint never existed).
 *
 * Covers: question, poll, quiz, exam (self-paced), NPS, word cloud,
 * SWOT/strategic frameworks. Crosstab / image activity hand off to the
 * browser with a QR + link card (their UIs need more room than the
 * 360px sidebar).
 */

import { useState, useCallback, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import type { SessionNode } from '../lib/api';
import {
  ensureParticipant,
  getActiveQuestion,
  getActivePoll,
  getActiveQuiz,
  getActiveNps,
  getActiveWordCloud,
  getActiveSwot,
  getActiveExam,
  submitQuestionResponse,
  submitPollResponse,
  submitQuizResponse,
  submitNpsResponse,
  submitWordCloudResponse,
  submitSwotResponse,
  submitExamResponse,
  type ActiveQuestion,
  type ActivePoll,
  type ActiveQuiz,
  type ActiveNps,
  type ActiveWordCloud,
  type ActiveSwot,
  type ActiveExam,
} from '../lib/participantApi';
import { buildJoinUrl } from '../lib/zoomActions';
import { getFrameworkCategories } from '../lib/frameworks';
import { LatencyIndicator } from './LatencyIndicator';

/* ------------------------------------------------------------------ */
/*  Type config                                                       */
/* ------------------------------------------------------------------ */

const TYPE_CONFIG: Record<string, { abbr: string; label: string; color: string }> = {
  question: { abbr: 'Q', label: 'Question', color: 'bg-blue-500' },
  poll: { abbr: 'P', label: 'Poll', color: 'bg-green-500' },
  quiz: { abbr: 'Qz', label: 'Quiz', color: 'bg-purple-500' },
  nps: { abbr: 'NPS', label: 'NPS', color: 'bg-orange-500' },
  crosstab: { abbr: 'CT', label: 'Cross Tab', color: 'bg-pink-500' },
  imageactivity: { abbr: 'IA', label: 'Image Activity', color: 'bg-cyan-500' },
  wordcloud: { abbr: 'WC', label: 'Word Cloud', color: 'bg-teal-500' },
  swotanalysis: { abbr: 'SA', label: 'Strategic Analysis', color: 'bg-indigo-500' },
  exam: { abbr: 'EX', label: 'Exam', color: 'bg-red-500' },
};

type Activity =
  | { kind: 'question'; data: ActiveQuestion }
  | { kind: 'poll'; data: ActivePoll }
  | { kind: 'quiz'; data: ActiveQuiz }
  | { kind: 'nps'; data: ActiveNps }
  | { kind: 'wordcloud'; data: ActiveWordCloud }
  | { kind: 'swotanalysis'; data: ActiveSwot }
  | { kind: 'exam'; data: ActiveExam }
  | { kind: 'unsupported'; data: null };

/** Types that deliberately hand off to the browser (sidebar is too small). */
const HANDOFF_COPY: Record<string, string> = {
  crosstab: 'Cross-tabs use a two-question grid that needs more room than the meeting sidebar.',
  imageactivity: 'Image activities are interactive (click or drag on a picture) and need a bigger screen.',
};

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

interface ZoomParticipantViewProps {
  sessionId: string;
  node: SessionNode | null;
  isPolling: boolean;
  displayName: string;
}

export function ZoomParticipantView({
  sessionId,
  node,
  isPolling,
  displayName,
}: ZoomParticipantViewProps) {
  const [activity, setActivity] = useState<Activity | null>(null);
  const [loading, setLoading] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);

  // Inputs
  const [textResponse, setTextResponse] = useState('');
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [npsScore, setNpsScore] = useState<number | null>(null);
  const [words, setWords] = useState<string[]>([]);
  const [wordInput, setWordInput] = useState('');
  const [swotCategory, setSwotCategory] = useState<string | null>(null);
  const [swotEntries, setSwotEntries] = useState(0);
  // Exam: self-paced local progression through questions
  const [examIndex, setExamIndex] = useState(0);
  const [examCorrect, setExamCorrect] = useState(0);
  const [examFinal, setExamFinal] = useState<{ score?: number; total?: number } | null>(null);
  const [copied, setCopied] = useState(false);

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* -- Join the session as a participant (mint token) ------------- */
  useEffect(() => {
    ensureParticipant(sessionId, displayName).catch((err) => {
      setJoinError(err instanceof Error ? err.message : 'Could not join session');
    });
  }, [sessionId, displayName]);

  /* -- Load the active activity when the node changes ------------- */
  useEffect(() => {
    setTextResponse('');
    setSelectedOptions([]);
    setNpsScore(null);
    setWords([]);
    setWordInput('');
    setSwotCategory(null);
    setSwotEntries(0);
    setExamIndex(0);
    setExamCorrect(0);
    setExamFinal(null);
    setCopied(false);
    setSubmitted(false);
    setSubmitting(false);
    setError(null);
    setActivity(null);

    if (!node) return;
    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        let next: Activity;
        switch (node.type) {
          case 'question': {
            const d = await getActiveQuestion(sessionId);
            next = d ? { kind: 'question', data: d } : { kind: 'unsupported', data: null };
            break;
          }
          case 'poll': {
            const d = await getActivePoll(sessionId);
            next = d ? { kind: 'poll', data: d } : { kind: 'unsupported', data: null };
            break;
          }
          case 'quiz': {
            const d = await getActiveQuiz(sessionId);
            next = d ? { kind: 'quiz', data: d } : { kind: 'unsupported', data: null };
            break;
          }
          case 'nps': {
            const d = await getActiveNps(sessionId);
            next = d ? { kind: 'nps', data: d } : { kind: 'unsupported', data: null };
            break;
          }
          case 'wordcloud': {
            const d = await getActiveWordCloud(sessionId);
            next = d ? { kind: 'wordcloud', data: d } : { kind: 'unsupported', data: null };
            break;
          }
          case 'swotanalysis': {
            const d = await getActiveSwot(sessionId);
            next = d ? { kind: 'swotanalysis', data: d } : { kind: 'unsupported', data: null };
            break;
          }
          case 'exam': {
            const d = await getActiveExam(sessionId);
            next = d ? { kind: 'exam', data: d } : { kind: 'unsupported', data: null };
            break;
          }
          default:
            next = { kind: 'unsupported', data: null };
        }
        if (!cancelled) setActivity(next);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load activity:', err);
          setError('Failed to load activity');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Quiz advances within one node — refetch when its question index moves
  }, [sessionId, node?.id, node?.type, (node?.data as any)?.currentQuestionIndex]);

  /* -- Submit ------------------------------------------------------ */
  const handleSubmit = useCallback(async () => {
    if (!activity || submitting) return;
    setSubmitting(true);
    setError(null);

    try {
      switch (activity.kind) {
        case 'question': {
          if (!textResponse.trim()) throw new Error('Please enter a response');
          await submitQuestionResponse(activity.data._id, textResponse.trim());
          break;
        }
        case 'poll': {
          if (selectedOptions.length === 0) throw new Error('Please select an option');
          await submitPollResponse(activity.data._id, selectedOptions);
          break;
        }
        case 'quiz': {
          if (selectedOptions.length === 0) throw new Error('Please select an answer');
          await submitQuizResponse(activity.data._id, selectedOptions[0]);
          break;
        }
        case 'nps': {
          if (npsScore === null) throw new Error('Please select a score');
          await submitNpsResponse(activity.data._id, npsScore);
          break;
        }
        case 'wordcloud': {
          const pending = wordInput.trim();
          const allWords = pending ? [...words, pending] : words;
          if (allWords.length === 0) throw new Error('Please add at least one word');
          await submitWordCloudResponse(activity.data._id, allWords);
          break;
        }
        case 'swotanalysis': {
          if (!swotCategory) throw new Error('Please pick a category');
          if (!textResponse.trim()) throw new Error('Please enter your point');
          await submitSwotResponse(activity.data._id, swotCategory, textResponse.trim());
          // Multi-entry: clear the text, stay open for more
          setSwotEntries((n) => n + 1);
          setTextResponse('');
          setSubmitting(false);
          return;
        }
        case 'exam': {
          // Self-paced: submit the current question, then advance locally.
          if (selectedOptions.length === 0) throw new Error('Please select an answer');
          const isLast = examIndex >= activity.data.questions.length - 1;
          const advance = (result?: { score?: number; total?: number }) => {
            if (isLast) {
              setExamFinal({ score: result?.score, total: result?.total });
              setSubmitted(true);
            } else {
              setExamIndex((i) => i + 1);
              setSelectedOptions([]);
            }
          };
          try {
            const result = await submitExamResponse(activity.data._id, examIndex, selectedOptions[0]);
            if (result?.isCorrect) setExamCorrect((n) => n + 1);
            advance(result);
          } catch (err) {
            // 409 = this question was already answered — e.g. the facilitator
            // reactivated the exam mid-flow and our local index reset to 0.
            // The server keeps the real answer; advance locally instead of
            // hard-locking the participant on a question they can't resubmit.
            if ((err as { status?: number }).status === 409) {
              advance();
            } else {
              throw err;
            }
          }
          setSubmitting(false);
          return;
        }
        default:
          throw new Error('This activity cannot be answered here');
      }
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit response');
    } finally {
      setSubmitting(false);
    }
  }, [activity, submitting, textResponse, selectedOptions, npsScore, words, wordInput, swotCategory, examIndex]);

  /* -- Word cloud helpers ------------------------------------------ */
  const addWord = useCallback(() => {
    const w = wordInput.trim();
    if (!w) return;
    setWords((prev) => {
      const max = activity?.kind === 'wordcloud' ? activity.data.maxSubmissions : 3;
      if (prev.length >= max) return prev;
      return [...prev, w];
    });
    setWordInput('');
  }, [wordInput, activity]);

  /* -- Empty / error states ---------------------------------------- */
  if (joinError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 px-4">
        <p className="text-sm font-medium text-gray-900 mb-1">Could not join session</p>
        <p className="text-xs text-gray-500 text-center">{joinError}</p>
      </div>
    );
  }

  if (!node) {
    return (
      <div className="flex flex-col items-center justify-center h-64 px-4">
        <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mb-3">
          <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <p className="text-sm text-gray-500 text-center">
          Waiting for the facilitator to start an activity...
        </p>
        <LatencyIndicator isPolling={isPolling} />
      </div>
    );
  }

  const cfg = TYPE_CONFIG[node.type] || { abbr: '?', label: 'Activity', color: 'bg-gray-500' };

  if (loading || (!activity && !error)) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent" />
      </div>
    );
  }

  /* -- Submitted state ---------------------------------------------- */
  if (submitted) {
    // Exam completion gets a score summary when the facilitator allows it;
    // otherwise the local correct-count is deliberately NOT shown (results
    // visibility is the facilitator's call via showResultsToParticipants).
    const finishedExam = activity?.kind === 'exam';
    const showScore = finishedExam && examFinal && typeof examFinal.score === 'number';
    return (
      <div className="flex flex-col items-center justify-center h-64 px-4">
        <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-3">
          <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <p className="text-sm font-medium text-gray-700 mb-1">
          {finishedExam ? 'Exam complete' : 'Response submitted'}
        </p>
        {showScore && (
          <p className="text-lg font-bold text-gray-900 mb-1">
            {examFinal!.score}/{examFinal!.total ?? (activity!.data as ActiveExam).questions.length}
          </p>
        )}
        <p className="text-xs text-gray-400">Waiting for the next activity...</p>
      </div>
    );
  }

  /* -- Load failure for an inline-capable activity ------------------ */
  // An inline type (question/poll/quiz/exam/nps/wordcloud/swot) that failed
  // to fetch leaves activity null WITH an error set. Show an honest error +
  // browser fallback — NOT the "needs a bigger screen" handoff, which is only
  // truthful for crosstab/image (those resolve to kind:'unsupported', below).
  if (!activity && error) {
    const joinUrl = buildJoinUrl(sessionId);
    return (
      <div className="flex flex-col items-center justify-center min-h-64 px-6 py-6 text-center">
        <p className="text-sm font-medium text-gray-800 mb-1">Couldn't load this activity</p>
        <p className="text-xs text-gray-500 mb-4">{error}</p>
        <a
          href={joinUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="px-4 py-2 text-xs font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors"
        >
          Open in browser
        </a>
      </div>
    );
  }

  /* -- Browser handoff: crosstab/image (and anything else the sidebar
        can't render) gets a QR + link card instead of a bare URL ------ */
  if (!activity || activity.kind === 'unsupported') {
    const joinUrl = buildJoinUrl(sessionId);
    const reason = HANDOFF_COPY[node.type];
    return (
      <div className="flex flex-col items-center justify-center min-h-64 px-6 py-6 text-center">
        <div className="flex items-center gap-2 mb-3">
          <span className={`${cfg.color} text-white text-[10px] font-bold rounded px-1.5 py-0.5`}>
            {cfg.abbr}
          </span>
          <span className="text-sm font-semibold text-gray-800">{cfg.label}</span>
        </div>
        <p className="text-xs text-gray-500 mb-4 max-w-[280px]">
          {reason ?? 'This activity needs a bigger screen than the meeting sidebar.'}
        </p>

        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm mb-3">
          <QRCodeSVG value={joinUrl} size={120} />
        </div>
        <p className="text-[11px] text-gray-400 mb-3">Scan with your phone, or:</p>

        <div className="flex gap-2">
          <a
            href={joinUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 text-xs font-medium text-white bg-primary-500 rounded-lg
              hover:bg-primary-600 transition-colors"
          >
            Open in browser
          </a>
          <button
            onClick={() => {
              navigator.clipboard?.writeText(joinUrl).catch(() => {});
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }}
            className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg
              hover:bg-gray-200 transition-colors"
          >
            {copied ? 'Copied!' : 'Copy link'}
          </button>
        </div>
        {error && <p className="mt-3 text-xs text-red-500">{error}</p>}
      </div>
    );
  }

  /* -- Per-type bodies ----------------------------------------------- */
  const prompt =
    activity.kind === 'question' ? activity.data.promptText
    : activity.kind === 'poll' ? activity.data.question
    : activity.kind === 'quiz' ? (activity.data.questions[activity.data.currentQuestionIndex]?.promptText ?? activity.data.title)
    : activity.kind === 'nps' ? activity.data.question
    : activity.kind === 'wordcloud' ? activity.data.text
    : activity.kind === 'exam' ? (activity.data.questions[examIndex]?.promptText ?? activity.data.title)
    : activity.data.title;

  const quizQuestion =
    activity.kind === 'quiz'
      ? activity.data.questions[activity.data.currentQuestionIndex]
      : null;

  const examQuestion =
    activity.kind === 'exam' ? activity.data.questions[examIndex] : null;

  const swotCats =
    activity.kind === 'swotanalysis'
      ? getFrameworkCategories(activity.data.frameworkType)
      : [];

  return (
    <div className="flex flex-col h-full max-w-[360px]">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-2">
          <span className={`${cfg.color} text-white text-[10px] font-bold rounded px-1.5 py-0.5`}>
            {cfg.abbr}
          </span>
          <span className="text-xs text-gray-500">{cfg.label}</span>
        </div>
        <LatencyIndicator isPolling={isPolling} />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        <h2 className="text-base font-semibold text-gray-900 mb-4">{prompt}</h2>

        {/* Question */}
        {activity.kind === 'question' && (
          <textarea
            className="w-full h-32 px-3 py-2.5 text-sm border border-gray-200 rounded-lg
              focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
              resize-none placeholder-gray-400"
            placeholder="Type your response..."
            value={textResponse}
            onChange={(e) => setTextResponse(e.target.value)}
            maxLength={2000}
          />
        )}

        {/* Poll */}
        {activity.kind === 'poll' && (
          <div className="space-y-2">
            {activity.data.allowMultiple && (
              <p className="text-[11px] text-gray-400">Select all that apply</p>
            )}
            {activity.data.options.map((opt, idx) => {
              const isSelected = selectedOptions.includes(opt.id);
              return (
                <button
                  key={opt.id}
                  onClick={() =>
                    setSelectedOptions((prev) =>
                      activity.data.allowMultiple
                        ? isSelected
                          ? prev.filter((id) => id !== opt.id)
                          : [...prev, opt.id]
                        : [opt.id],
                    )
                  }
                  className={`w-full text-left px-4 py-3 text-sm rounded-lg border transition-all ${
                    isSelected
                      ? 'bg-green-50 border-green-300 text-green-800 ring-1 ring-green-300'
                      : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300'
                  }`}
                >
                  <span className="font-medium mr-2 text-gray-400">
                    {String.fromCharCode(65 + idx)}.
                  </span>
                  {opt.text}
                </button>
              );
            })}
          </div>
        )}

        {/* Quiz (current question) */}
        {activity.kind === 'quiz' && quizQuestion && (
          <div className="space-y-2">
            {quizQuestion.options.map((opt, idx) => {
              const optId = opt.id || opt._id || String(idx);
              const isSelected = selectedOptions[0] === optId;
              return (
                <button
                  key={optId}
                  onClick={() => setSelectedOptions([optId])}
                  className={`w-full text-left px-4 py-3 text-sm rounded-lg border transition-all ${
                    isSelected
                      ? 'bg-purple-50 border-purple-300 text-purple-800 ring-1 ring-purple-300'
                      : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300'
                  }`}
                >
                  <span className="font-medium mr-2 text-gray-400">
                    {String.fromCharCode(65 + idx)}.
                  </span>
                  {opt.text}
                </button>
              );
            })}
          </div>
        )}

        {/* Exam (self-paced — participant advances at their own speed) */}
        {activity.kind === 'exam' && examQuestion && (
          <div className="space-y-2">
            <div className="flex items-center justify-between mb-1">
              <p className="text-[11px] text-gray-400">
                Question {examIndex + 1} of {activity.data.questions.length}
              </p>
              {activity.data.questions.length <= 12 && (
                <div className="flex gap-1">
                  {activity.data.questions.map((_, i) => (
                    <span
                      key={i}
                      className={`w-1.5 h-1.5 rounded-full ${
                        i < examIndex ? 'bg-red-400' : i === examIndex ? 'bg-red-600' : 'bg-gray-200'
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
            {examQuestion.options.map((opt, idx) => {
              const optId = opt.id || opt._id || String(idx);
              const isSelected = selectedOptions[0] === optId;
              return (
                <button
                  key={optId}
                  onClick={() => setSelectedOptions([optId])}
                  className={`w-full text-left px-4 py-3 text-sm rounded-lg border transition-all ${
                    isSelected
                      ? 'bg-red-50 border-red-300 text-red-800 ring-1 ring-red-300'
                      : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300'
                  }`}
                >
                  <span className="font-medium mr-2 text-gray-400">
                    {String.fromCharCode(65 + idx)}.
                  </span>
                  {opt.text}
                </button>
              );
            })}
          </div>
        )}

        {/* NPS */}
        {activity.kind === 'nps' && (
          <div>
            <div className="grid grid-cols-11 gap-1 mb-2">
              {Array.from({ length: 11 }, (_, i) => (
                <button
                  key={i}
                  onClick={() => setNpsScore(i)}
                  className={`aspect-square flex items-center justify-center text-xs font-medium
                    rounded-lg border transition-all ${
                      npsScore === i
                        ? i <= 6
                          ? 'bg-red-100 border-red-300 text-red-700 ring-1 ring-red-300'
                          : i <= 8
                          ? 'bg-yellow-100 border-yellow-300 text-yellow-700 ring-1 ring-yellow-300'
                          : 'bg-green-100 border-green-300 text-green-700 ring-1 ring-green-300'
                        : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                >
                  {i}
                </button>
              ))}
            </div>
            <div className="flex justify-between text-[10px] text-gray-400 px-1">
              <span>Not likely</span>
              <span>Very likely</span>
            </div>
          </div>
        )}

        {/* Word cloud */}
        {activity.kind === 'wordcloud' && (
          <div>
            <p className="text-[11px] text-gray-400 mb-2">
              Add up to {activity.data.maxSubmissions} words ({words.length}/
              {activity.data.maxSubmissions})
            </p>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg
                  focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
                  placeholder-gray-400"
                placeholder="Type a word..."
                value={wordInput}
                maxLength={40}
                onChange={(e) => setWordInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addWord();
                  }
                }}
              />
              <button
                onClick={addWord}
                disabled={!wordInput.trim() || words.length >= activity.data.maxSubmissions}
                className="px-3 py-2 text-sm font-medium bg-gray-100 text-gray-700 rounded-lg
                  hover:bg-gray-200 disabled:opacity-40 transition-colors"
              >
                Add
              </button>
            </div>
            {words.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {words.map((w, i) => (
                  <span
                    key={`${w}-${i}`}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-teal-50
                      text-teal-700 border border-teal-200 rounded-full"
                  >
                    {w}
                    <button
                      onClick={() => setWords((prev) => prev.filter((_, idx) => idx !== i))}
                      className="text-teal-400 hover:text-teal-600"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* SWOT / strategic frameworks */}
        {activity.kind === 'swotanalysis' && (
          <div>
            <div className={`grid gap-2 mb-3 ${swotCats.length <= 4 ? 'grid-cols-2' : 'grid-cols-2'}`}>
              {swotCats.map((cat) => (
                <button
                  key={cat.key}
                  onClick={() => setSwotCategory(cat.key)}
                  className={`px-2 py-2 text-xs font-medium rounded-lg border transition-all ${
                    swotCategory === cat.key
                      ? 'bg-indigo-50 border-indigo-300 text-indigo-800 ring-1 ring-indigo-300'
                      : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
            <textarea
              className="w-full h-20 px-3 py-2.5 text-sm border border-gray-200 rounded-lg
                focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
                resize-none placeholder-gray-400"
              placeholder={swotCategory ? 'Add your point...' : 'Pick a category first...'}
              value={textResponse}
              onChange={(e) => setTextResponse(e.target.value)}
              maxLength={500}
            />
            {swotEntries > 0 && (
              <p className="mt-1 text-[11px] text-green-600">
                {swotEntries} {swotEntries === 1 ? 'entry' : 'entries'} submitted — add another?
              </p>
            )}
          </div>
        )}

        {error && <p className="mt-3 text-xs text-red-500">{error}</p>}
      </div>

      {/* Submit */}
      <div className="px-4 py-3 border-t border-gray-200 bg-white">
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full py-2.5 text-sm font-medium text-white bg-primary-500 rounded-lg
            hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed
            transition-colors"
        >
          {submitting
            ? 'Submitting...'
            : activity.kind === 'swotanalysis'
            ? 'Add entry'
            : activity.kind === 'exam'
            ? (examIndex >= activity.data.questions.length - 1 ? 'Finish exam' : 'Next question')
            : 'Submit'}
        </button>
      </div>
    </div>
  );
}
